import {
    OptionData,
    Question,
    OPTION_TYPES,
    ProjectLanguage,
    QTYPES,
    PlainTextContent
} from '@nexxt/common/types';
import * as React from 'react';
import * as classnames from 'classnames';
import { SortableElement } from 'react-sortable-hoc';
import {
    UilTrashAlt,
    UilPen,
    UilLockAlt,
    UilImagePlus,
    UilCommentQuestion,
    UilCodeBranch,
    UilMultiply,
    UilExclamationTriangle
} from '@iconscout/react-unicons';
import { faRandom } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactTooltip from 'react-tooltip';
import { NA_OPTION_VALUE, OTHER_OPTION_VALUE } from '@nexxt/common/constants';
import Popover from 'react-tiny-popover';
import PopoverMenu from '../../../../../common/PopoverMenu';
import MediaManager from '../../../../../libraries/mediaLibrary/MediaManager';
import { getFilenameWithoutExtension } from '../../../../../../utils/string';
import { getEmptyOptionTexts } from '../../buildOptions';
import { imageDisplay } from '../../../../../../mapping/optionSettings';
import { isEmpty } from 'lodash';
import { getMainLanguage } from '../../../../../../utils/project';
import { getConfirmationBox } from '../../../../../common/swal/Sweetalert';

interface OptionProps {
    languages: ProjectLanguage[];
    question: Question;
    position?: number;
    option?: OptionData;
    optionType: OPTION_TYPES;
    showAdvancedMode: boolean;
    index: number;
    optionDisabled?: boolean;
    isBuiltInOption?: boolean;
    shape?: 'rect' | 'round';
    errorObject?: any;
    showError: boolean;
    settings?: {
        forceMultiple?: boolean;
        hideMultiple?: boolean;
        showMultiplePopup?: boolean;
        maxOptionNum?: number;
        hideNotApplicable?: boolean;
        hideOther?: boolean;
        forceOptionType?: OPTION_TYPES;
        hideOptionSettings?: boolean;
        disableDnd?: boolean;
        hideAdvanceMode?: boolean;
        hideFooter?: boolean;
        disableMenu?: boolean;
        useTranslation?: boolean;
    };
    showProjectHealth?: boolean;
    conditionModalSetting?: any;
    defaultText?: string;
    handleOptions?: (options: OptionData[]) => void;
    addNewOption?: (position: number, data?: Partial<OptionData>) => void;
    updateOption?: (keyName: string, value: any) => void;
    updateOptionSettings?: (keyName: string, value: any) => void;
    updateOptionTextsByLanguage?: (value: string, language: string) => void;
    deleteOption: () => Promise<void>;
    setConditionModalSetting?: (settings) => void;
    isLive?: boolean;
    existsInPreviousPackage?: boolean;
}

const Option: React.FC<OptionProps> = ({
    index,
    languages,
    question,
    optionType,
    showAdvancedMode,
    option,
    isBuiltInOption,
    optionDisabled,
    shape = 'rect',
    position,
    errorObject,
    showError,
    settings,
    showProjectHealth,
    conditionModalSetting,
    addNewOption,
    updateOption,
    updateOptionSettings,
    updateOptionTextsByLanguage,
    deleteOption,
    handleOptions,
    setConditionModalSetting,
    isLive,
    existsInPreviousPackage
}: OptionProps) => {
    const showImage = [
        OPTION_TYPES.IMAGE_ONLY,
        OPTION_TYPES.IMAGE_AND_TEXT
    ].includes(optionType as OPTION_TYPES);
    const imageOnly = optionType === OPTION_TYPES.IMAGE_ONLY;
    const showText = [
        OPTION_TYPES.TEXT_ONLY,
        OPTION_TYPES.IMAGE_AND_TEXT
    ].includes(optionType as OPTION_TYPES);
    const onlyText = optionType === OPTION_TYPES.TEXT_ONLY;
    const [showPopover, setShowPopover] = React.useState(false);
    const [popoverPosition, setPopoverPosition] = React.useState([0, 0]);
    const [showMediaLibrary, setShowMediaLibrary] = React.useState(false);

    const [touched, setTouched] = React.useState({
        label: false,
        texts: {},
        mediaURL: false,
        value: false
    });

    const toggleRandomize = () => {
        updateOption('randomizeOption', !option.randomizeOption);
    };

    const setDirty = (keyName: string) => {
        setTouched((prevTouched) => ({ ...prevTouched, [keyName]: true }));
    };

    const applyMediaToSingleOption = (data) => {
        updateOption('mediaURL', data.mediaURL);
        // For Other option, don't need to update texts/label
        if (isBuiltInOption) {
            return;
        }
        const labelWithoutExtension = getFilenameWithoutExtension(data?.label);
        const mainLanguage = getMainLanguage(languages);
        const texts = option.texts.slice() || getEmptyOptionTexts();
        const newTexts = texts.map((text) => {
            if (text.language === mainLanguage) {
                const currentText = (text?.contents[0] as PlainTextContent)
                    ?.text;
                if (!currentText) {
                    return {
                        ...text,
                        contents: [
                            {
                                ...text?.contents[0],
                                text: labelWithoutExtension
                            }
                        ]
                    };
                }
            }
            return text;
        });

        updateOption('label', labelWithoutExtension);
        updateOption('texts', newTexts);
    };

    const getOptionTextPlaceholder = (): string => {
        switch (option.value) {
            case NA_OPTION_VALUE:
                return 'Not Applicable';
            case OTHER_OPTION_VALUE:
                return 'Other';
            default:
                return 'Option text here...';
        }
    };
    const getErrorMessage = (): string[] => {
        const missingFields = [];
        if ((showError || touched.label) && errorObject?.label) {
            missingFields.push(errorObject.label);
        }

        if ((showError || touched.value) && errorObject?.value) {
            missingFields.push(errorObject.value);
        }

        if ((showError || touched.mediaURL) && errorObject?.mediaURL) {
            missingFields.push(errorObject.mediaURL);
        }

        if (errorObject?.texts?.isError) {
            languages?.forEach((l) => {
                if (
                    (showError || touched?.texts?.[l.value]) &&
                    errorObject?.texts?.[l.value]
                ) {
                    missingFields.push(`Missing text [${l.value}]`);
                }
            });
        }

        if (showProjectHealth) {
            missingFields.push('Option conditions require attention');
        }
        return missingFields;
    };

    const toggleConditionModal = async (evt) => {
        if (
            conditionModalSetting.show &&
            conditionModalSetting.isEdit &&
            conditionModalSetting.editFlag
        ) {
            const { isConfirmed } = await getConfirmationBox({
                showCloseButton: true,
                showCancelButton: true,
                title: 'Switch condition?',
                message: 'Changes won’t be saved. Do you want to proceed?'
            });
            if (!isConfirmed) {
                return;
            }
        }

        setConditionModalSetting({
            isEdit: option.conditions?.length ? false : true,
            show: true,
            activeOption: option,
            editFlag: false
        });
    };

    const handleMedia = (medias: any, hasMultipleMedia = false) => {
        if (hasMultipleMedia) {
            const options = question.options.slice();
            medias.forEach((media, i) => {
                const label = getFilenameWithoutExtension(media.label);
                options.push({
                    label,
                    value: '0',
                    mediaURL: media.mediaURL,
                    texts: getEmptyOptionTexts(languages),
                    randomizeOption: false,
                    empty: false
                });
            });

            const newOptions = options.map((opt, ind) => ({
                ...opt,
                value: `${ind + 1}`
            }));
            handleOptions(newOptions);
        }
    };

    const hasLogic =
        question.options.filter(
            (option) => option.conditions?.length > 0 || option.randomizeOption
        ).length > 0;

    const notApplicable = option?.value === NA_OPTION_VALUE;
    const hideDeleteButton = existsInPreviousPackage && isLive;
    const errorMessages = getErrorMessage();
    const popoverItems = [
        {
            label: 'Insert Option Before',
            action: (e) => {
                addNewOption(position);
                setShowPopover(false);
                setPopoverPosition([0, 0]);
            }
        },
        {
            label: 'Insert Option After',
            action: (e) => {
                addNewOption(position + 1);
                setShowPopover(false);
                setPopoverPosition([0, 0]);
            }
        },
        {
            label: 'Add Logic',
            action: (e) => {
                setShowPopover(false);
                setPopoverPosition([0, 0]);
                toggleConditionModal(e);
            }
        },
        {
            label: 'Delete Option',
            action: (e) => {
                deleteOption();
                setShowPopover(false);
                setPopoverPosition([0, 0]);
            }
        }
    ].filter((item) => !(hideDeleteButton && item.label == 'Delete Option'));
    return (
        <Popover
            isOpen={showPopover}
            onClickOutside={() => setShowPopover(false)}
            align="start"
            position="bottom"
            contentLocation={{
                top: popoverPosition[1],
                left: popoverPosition[0]
            }}
            content={<PopoverMenu items={popoverItems} />}
        >
            <div
                onContextMenu={(e) => {
                    if (
                        ![NA_OPTION_VALUE, OTHER_OPTION_VALUE].includes(
                            option.value
                        ) &&
                        !settings?.disableMenu &&
                        !optionDisabled
                    ) {
                        e.preventDefault();
                        setShowPopover(false);
                        setPopoverPosition([e.clientX, e.clientY]);
                        setShowPopover(true);

                        return false;
                    }
                }}
                className={classnames('option--container', {
                    round: shape === 'round' || notApplicable,
                    disabled: optionDisabled,
                    text: onlyText,
                    [`image--md`]: showImage,
                    error: showError && errorObject?.isError
                })}
                data-tip="custom show"
                data-for={`option-${option.value}-wrapper-helper`}
                data-delay-show="500"
            >
                {question.piping && (
                    <ReactTooltip
                        className="tooltip"
                        id={`option-${option.value}-wrapper-helper`}
                        ara-haspopup="true"
                        effect="solid"
                        place="top"
                    >
                        <span>Piped options cannot be edited</span>
                    </ReactTooltip>
                )}
                {showImage && !notApplicable && (
                    <div className="option--row centered">
                        <MediaManager
                            type="image"
                            showMediaManager={showMediaLibrary}
                            toggleMediaManager={setShowMediaLibrary}
                            applyCurrentMedia={applyMediaToSingleOption}
                            onConfirm={
                                question.type === QTYPES.TRADE_OFF &&
                                handleMedia
                            }
                            isMultiple={question.type === QTYPES.TRADE_OFF}
                            showMediaLibrary
                        >
                            <div
                                className={classnames('option--image', {
                                    'with-image': !!option?.mediaURL,
                                    [`with-image--md`]: showImage,
                                    [`round`]:
                                        question.imageDisplay ===
                                        imageDisplay[1].value,
                                    error:
                                        (touched.mediaURL || showError) &&
                                        errorObject?.mediaURL,
                                    disabled: optionDisabled
                                })}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!optionDisabled) {
                                        setDirty('mediaURL');
                                        setShowMediaLibrary(true);
                                    }
                                }}
                            >
                                {option?.mediaURL ? (
                                    <img src={option.mediaURL} />
                                ) : (
                                    <UilImagePlus size={30} />
                                )}
                                <div
                                    className={classnames('hover-container', {
                                        disabled: optionDisabled
                                    })}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMediaLibrary(true);
                                    }}
                                >
                                    <div
                                        className={classnames(
                                            'hover-container--item',
                                            {
                                                disabled: optionDisabled
                                            }
                                        )}
                                    >
                                        <UilPen size={16} />
                                    </div>
                                </div>
                            </div>
                        </MediaManager>
                    </div>
                )}

                <div className="option--row">
                    {showAdvancedMode && (
                        <input
                            placeholder={option?.value}
                            className={classnames('option--value', {
                                error:
                                    (touched.value || showError) &&
                                    errorObject?.value
                            })}
                            type="number"
                            min={0}
                            value={option.value}
                            onBlur={() => setDirty('value')}
                            onChange={(e) => {
                                setDirty('value');
                                updateOption('value', e.target.value);
                            }}
                            disabled={true}
                        />
                    )}
                    {showText ? (
                        <div className="option--row--wrapper column">
                            {languages?.map((language, index) => {
                                const languageCode = language.value;
                                const texts =
                                    (
                                        option?.texts?.find(
                                            (text) =>
                                                text.language === languageCode
                                        )?.contents?.[0] as PlainTextContent
                                    )?.text || '';

                                const specifyPadding =
                                    option.value === OTHER_OPTION_VALUE &&
                                    question.optionSettings?.showSpecify
                                        ? 68
                                        : 0;
                                const errorPadding =
                                    (touched?.texts[languageCode] ||
                                        showError) &&
                                    (errorObject?.texts || {})[languageCode]
                                        ? 26
                                        : 0;
                                const langPadding =
                                    languages.length > 1 ? 26 : 0;

                                return (
                                    <div
                                        className={classnames(
                                            'option--text--wrapper',
                                            {
                                                disabled: optionDisabled
                                            }
                                        )}
                                        key={`${option?.value}-${language.value}`}
                                    >
                                        <OptionTextArea
                                            texts={texts}
                                            optionDisabled={optionDisabled}
                                            touched={touched}
                                            showError={showError}
                                            errorObject={errorObject}
                                            languageCode={languageCode}
                                            getOptionTextPlaceholder={
                                                getOptionTextPlaceholder
                                            }
                                            setTouched={setTouched}
                                            updateOptionTextsByLanguage={
                                                updateOptionTextsByLanguage
                                            }
                                            paddingRight={
                                                specifyPadding +
                                                errorPadding +
                                                langPadding
                                            }
                                        />
                                        <div className="option--text--inner">
                                            {index === 0 &&
                                                option.value ===
                                                    OTHER_OPTION_VALUE &&
                                                question.optionSettings
                                                    ?.showSpecify && (
                                                    <span
                                                        className={classnames(
                                                            'option--specify',
                                                            {
                                                                disabled:
                                                                    optionDisabled
                                                            }
                                                        )}
                                                    >
                                                        Specify
                                                        {!optionDisabled && (
                                                            <UilMultiply
                                                                size={10}
                                                                onClick={() =>
                                                                    !optionDisabled &&
                                                                    updateOptionSettings(
                                                                        'showSpecify',
                                                                        false
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </span>
                                                )}
                                            {languages.length > 1 && (
                                                <span
                                                    className={classnames(
                                                        'option--text--lang',
                                                        {
                                                            error:
                                                                (touched.texts[
                                                                    languageCode
                                                                ] ||
                                                                    showError) &&
                                                                errorObject?.texts &&
                                                                errorObject
                                                                    ?.texts?.[
                                                                    languageCode
                                                                ]
                                                        }
                                                    )}
                                                >
                                                    {languageCode}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div
                            className="option--label--wrapper"
                            data-tip="custom show"
                            data-for={`option-${option.value}-label-helper`}
                            data-delay-show="500"
                        >
                            <span className="option--label--hash">#</span>
                            <input
                                className={classnames('option--label', {
                                    disabled: optionDisabled
                                })}
                                placeholder="option label"
                                type="text"
                                value={option?.label}
                                onBlur={() => setDirty('label')}
                                onChange={(e) => {
                                    updateOption('label', e.target.value);
                                    setDirty('label');
                                }}
                                disabled={
                                    optionDisabled ||
                                    option.value === OTHER_OPTION_VALUE
                                }
                            />
                            {!optionDisabled && (
                                <ReactTooltip
                                    className="tooltip"
                                    id={`option-${option.value}-label-helper`}
                                    ara-haspopup="true"
                                    effect="solid"
                                    place="top"
                                >
                                    <span>
                                        {option.value === OTHER_OPTION_VALUE
                                            ? 'Other option label cannot be edited'
                                            : 'Option label that is displayed on your dashboard'}
                                    </span>
                                </ReactTooltip>
                            )}
                        </div>
                    )}
                    {((!question.piping &&
                        option.value === OTHER_OPTION_VALUE &&
                        !question.optionSettings?.showSpecify) ||
                        (imageOnly && option.value === OTHER_OPTION_VALUE)) && (
                        <button
                            className={classnames('option--icon specify', {
                                disabled: optionDisabled,
                                active: question.optionSettings?.showSpecify
                            })}
                            onClick={() =>
                                updateOptionSettings(
                                    'showSpecify',
                                    !question.optionSettings?.showSpecify
                                )
                            }
                            data-tip="custom show"
                            data-for={`option-${option.value}-specify-helper`}
                            data-delay-show="500"
                        >
                            <UilCommentQuestion size={16} />
                            <ReactTooltip
                                className="tooltip"
                                id={`option-${option.value}-specify-helper`}
                                ara-haspopup="true"
                                effect="solid"
                                place="top"
                            >
                                <span>
                                    Enable participants to specify what they
                                    mean by other.
                                </span>
                            </ReactTooltip>
                        </button>
                    )}
                    {errorMessages?.length && !hasLogic && !showAdvancedMode ? (
                        <>
                            <ReactTooltip
                                className="tooltip"
                                id={`option-${option.value}-error`}
                                ara-haspopup="true"
                                effect="solid"
                                place="top"
                            >
                                <>
                                    {errorMessages.map((message, index) => (
                                        <p key={message + index}>{message};</p>
                                    ))}
                                </>
                            </ReactTooltip>
                            <span
                                data-tip="custom show"
                                data-for={`option-${option.value}-error`}
                                data-delay-show="500"
                                className="option--text--error"
                            >
                                <UilExclamationTriangle size={16} />
                            </span>
                        </>
                    ) : null}
                    {((notApplicable && !optionDisabled && !hideDeleteButton) ||
                        (!showAdvancedMode &&
                            !hasLogic &&
                            !optionDisabled &&
                            !hideDeleteButton)) && (
                        <>
                            <div
                                className="option--icon delete"
                                onClick={deleteOption}
                                data-tip="custom show"
                                data-for={`option-${option.value}-delete-helper`}
                                data-delay-show="500"
                            >
                                <UilTrashAlt size={16} />
                                <ReactTooltip
                                    className="tooltip"
                                    id={`option-${option.value}-delete-helper`}
                                    ara-haspopup="true"
                                    effect="solid"
                                    place="top"
                                >
                                    <span>Delete option</span>
                                </ReactTooltip>
                            </div>
                        </>
                    )}
                </div>
                {showAdvancedMode && showText && (
                    <div className="option--row">
                        <div
                            className={classnames('option--label--wrapper', {
                                error:
                                    (touched.label || showError) &&
                                    errorObject?.label
                            })}
                            data-tip="custom show"
                            data-for={`option-${option.value}-label-helper`}
                            data-delay-show="500"
                        >
                            <span className="option--label--hash">#</span>
                            <input
                                className={classnames('option--label', {
                                    disabled:
                                        optionDisabled ||
                                        option.value === OTHER_OPTION_VALUE,
                                    error:
                                        (touched.label || showError) &&
                                        errorObject?.label
                                })}
                                placeholder="option label"
                                type="text"
                                value={option?.label}
                                onBlur={() => {
                                    setDirty('label');
                                    if (isEmpty(option?.label)) {
                                        const l = getMainLanguage(languages);
                                        const mainLanguageIndex =
                                            option.texts.findIndex(
                                                (t) => t.language == l
                                            );

                                        updateOption(
                                            'label',
                                            (
                                                option.texts?.[
                                                    mainLanguageIndex
                                                ]
                                                    ?.contents?.[0] as PlainTextContent
                                            ).text || ''
                                        );
                                    }
                                }}
                                onChange={(e) => {
                                    updateOption('label', e.target.value);
                                    setDirty('label');
                                }}
                                disabled={
                                    optionDisabled ||
                                    option.value === OTHER_OPTION_VALUE
                                }
                            />
                            {!optionDisabled && (
                                <ReactTooltip
                                    className="tooltip"
                                    id={`option-${option.value}-label-helper`}
                                    ara-haspopup="true"
                                    effect="solid"
                                    place="top"
                                >
                                    <span>
                                        {option.value === OTHER_OPTION_VALUE
                                            ? 'Other option label cannot be edited'
                                            : 'Option label that is displayed on your dashboard'}
                                    </span>
                                </ReactTooltip>
                            )}
                        </div>
                    </div>
                )}
                {!notApplicable && (showAdvancedMode || hasLogic) && (
                    <div className="option--row justify-content-between">
                        <div className="option--row--wrapper">
                            {(showAdvancedMode || option.randomizeOption) && (
                                <span
                                    className={classnames(
                                        'option--icon random',
                                        {
                                            locked: !option.randomizeOption,
                                            disabled: !question.random
                                        }
                                    )}
                                    data-tip="custom show"
                                    data-for={`option-${option.value}-randomized-helper`}
                                    data-delay-show="500"
                                >
                                    {question.random ? (
                                        <FontAwesomeIcon
                                            icon={faRandom}
                                            onClick={() =>
                                                showAdvancedMode &&
                                                toggleRandomize()
                                            }
                                        />
                                    ) : (
                                        <UilLockAlt
                                            size={20}
                                            onClick={toggleRandomize}
                                        />
                                    )}
                                    <ReactTooltip
                                        className="tooltip"
                                        id={`option-${option.value}-randomized-helper`}
                                        ara-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        <span>
                                            {!question.random
                                                ? 'Enable randomize in option settings'
                                                : `The display order will ${
                                                      !option.randomizeOption
                                                          ? 'not'
                                                          : ''
                                                  } be randomized. ${
                                                      showAdvancedMode
                                                          ? ''
                                                          : 'Turn on Advanced Mode to disable randomize'
                                                  }`}
                                        </span>
                                    </ReactTooltip>
                                </span>
                            )}
                            {!isBuiltInOption &&
                            (showAdvancedMode || option.conditions?.length) ? (
                                <span
                                    className={classnames(
                                        'option--icon add-logic',
                                        {
                                            'with-logic':
                                                option.conditions?.length
                                        }
                                    )}
                                    data-tip="custom show"
                                    data-for={`option-${option.value}-logic-helper`}
                                    data-delay-show="500"
                                    onClick={toggleConditionModal}
                                >
                                    <UilCodeBranch size={16} />
                                    <ReactTooltip
                                        className="tooltip"
                                        id={`option-${option.value}-logic-helper`}
                                        ara-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        <span>Click to see display logic</span>
                                    </ReactTooltip>
                                </span>
                            ) : null}
                        </div>
                        <div className="option--row--wrapper justify-content-end">
                            {errorMessages?.length ? (
                                <>
                                    <ReactTooltip
                                        className="tooltip"
                                        id={`option-${option.value}-error`}
                                        ara-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        <>
                                            {errorMessages.map(
                                                (message, index) => (
                                                    <p key={message + index}>
                                                        {message};
                                                    </p>
                                                )
                                            )}
                                        </>
                                    </ReactTooltip>
                                    <span
                                        data-tip="custom show"
                                        data-for={`option-${option.value}-error`}
                                        data-delay-show="500"
                                        className="option--text--error"
                                    >
                                        <UilExclamationTriangle size={16} />
                                    </span>
                                </>
                            ) : null}
                            {!optionDisabled && !hideDeleteButton && (
                                <div
                                    className="option--icon delete"
                                    onClick={deleteOption}
                                    data-tip="custom show"
                                    data-for={`option-${option.value}-delete-helper`}
                                    data-delay-show="500"
                                >
                                    <UilTrashAlt size={16} />
                                    <ReactTooltip
                                        className="tooltip"
                                        id={`option-${option.value}-delete-helper`}
                                        ara-haspopup="true"
                                        effect="solid"
                                        place="top"
                                    >
                                        <span>Delete option</span>
                                    </ReactTooltip>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Popover>
    );
};

interface OptionTextAreaProps {
    texts: any;
    optionDisabled: any;
    touched: any;
    showError: boolean;
    errorObject: any;
    languageCode: any;
    getOptionTextPlaceholder: any;
    setTouched: any;
    updateOptionTextsByLanguage: any;
    paddingRight: number;
}

const OptionTextArea: React.FC<OptionTextAreaProps> = ({
    texts,
    optionDisabled,
    touched,
    showError,
    errorObject,
    languageCode,
    getOptionTextPlaceholder,
    setTouched,
    updateOptionTextsByLanguage,
    paddingRight
}: OptionTextAreaProps) => {
    const [isEditingText, setIsEditingText] = React.useState(false);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const [textAreaHeight, setTextAreaHeight] = React.useState('22px');
    const [parentHeight, setParentHeight] = React.useState('22px');
    React.useEffect(() => {
        if (textAreaRef.current) {
            setParentHeight(`${textAreaRef.current!.scrollHeight - 5}px`);
            setTextAreaHeight(`${textAreaRef.current!.scrollHeight - 5}px`);
        }
    }, []);

    return (
        <div
            className={classnames(
                'option--text option--text--textarea--wrapper',
                {
                    empty: !texts,
                    editing: !optionDisabled && isEditingText,
                    disabled: optionDisabled,
                    error:
                        (touched?.texts[languageCode] || showError) &&
                        (errorObject?.texts || {})[languageCode]
                }
            )}
            onMouseDown={(e) => {
                if (!optionDisabled) {
                    setIsEditingText(true);
                }
            }}
            style={{
                minHeight: parentHeight
            }}
        >
            <textarea
                autoFocus
                ref={textAreaRef}
                placeholder={getOptionTextPlaceholder()}
                className="option--text option--text--textarea"
                style={{
                    paddingRight,
                    height: textAreaHeight
                }}
                value={texts}
                onBlur={() => {
                    setTouched((touched) => ({
                        ...touched,
                        texts: {
                            ...touched.texts,
                            [languageCode]: true
                        }
                    }));
                    setIsEditingText(false);
                }}
                onChange={(e) => {
                    setTouched((touched) => ({
                        ...touched,
                        texts: {
                            ...touched.texts,
                            [languageCode]: true
                        }
                    }));
                    updateOptionTextsByLanguage(e.target.value, languageCode);
                    setParentHeight(
                        `${textAreaRef.current!.scrollHeight - 5}px`
                    );
                    setTextAreaHeight(
                        `${textAreaRef.current!.scrollHeight - 5}px`
                    );
                    setIsEditingText(true);
                }}
                disabled={optionDisabled}
            />
        </div>
    );
};

export default SortableElement(Option);
